import { SlidersHorizontal } from '@phosphor-icons/react';
import { Button, FilterButton, getDateTimeText, TagFilter, VText } from '@vake/ui-kit';
import { Feature, FeatureCollection } from 'geojson';
import { FC, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { getLegendDot } from '../../../shared/components/object-class-dot.tsx';
import { DetectionImage, PlanetThumbnail, TargetOfInterestImage } from '../../../shared/components/Popup';
import { elevatedTags } from '../../../shared/constants.ts';
import { mmsiToFlag } from '../../../utils/mmsi-to-flag.tsx';
import { ImageTransform } from '../../../utils/transform.tsx';
import { Arrow } from '../interactiveReport/reportInfo/ReportInfo';
import { Border, BorderFreeRounded, ColumnWrapper } from '../styles/Wrappers';
import { VesselInfo } from './overwatch-vessel-info.tsx';
import { TagView } from './tags.tsx';

export const SideBar = styled.div`
    position: fixed;
    top: 50px;
    right: 5px;
    z-index: 1000;
    min-width: 175px;
    width: 20%;
    max-width: 325px;
    max-height: calc(100vh - 60px);
`;

const ImageHolder = styled.div<{ numItems: number }>`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: ${(props) => (props.numItems < 5 ? '' : 'space-between')};
    height: 60vh;
    overflow-y: scroll;
    // padding-left: 5px;
`;

const ImageObjectWrapper = styled.span<{ $isSentinel?: boolean | null }>`
    display: flex;
    width: 95%;
    align-items: center;
    justify-content: space-between;
    border-radius: 5px;
    border: 2px solid transparent;
    background-color: ${(props) => (props.$isSentinel ? 'default' : '#1178aa')};
    &:hover {
        border: 2px solid #ecda80;
    }
    gap: 5px;
`;
const ImageWrapper = styled.div`
    width: 120px;
    height: 120px;
    display: inline-block;
    overflow: hidden;
    // width: 100%; /* Adjusts the width to fill the container */
    // height: auto; /* Maintains the aspect ratio */
    // object-fit: cover; /* Ensures the image covers the container without distortion */
    border-radius: 3px;
`;

const DateInfo = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    flex-grow: 1;
`;
export const OverwatchRight: FC<{
    aisPositions: FeatureCollection | undefined;
    matchedVessels: FeatureCollection | undefined;
    darkVessels: FeatureCollection | undefined;
    staticObjects: FeatureCollection | undefined;
    selectedFeature: Feature | null;
    setSelectedFeature: (value: Feature | null) => void;
    setHoveredFeature: (value: Feature | null) => void;
    shipName: string | null;
    detectionConfidence: number;
    url: string;
    processingStatus: boolean;
}> = ({
    aisPositions,
    matchedVessels,
    darkVessels,
    staticObjects,
    selectedFeature,
    setSelectedFeature,
    setHoveredFeature,
    shipName,
    detectionConfidence,
    processingStatus,
    url,
}) => {
    const [positions, setPositions] = useState<Feature[]>([]);
    const [numCaptures, setNumCaptures] = useState<number | null>(null);
    const [startIndex, setStartIndex] = useState<number>(0);
    const [endIndex, setEndIndex] = useState<number>(-1);
    const [isFilterOpen, setIsFilterOpen] = useState(false);

    const buttonIncrement = 30;

    const all_positions = useMemo(() => {
        const filtered_dark_vessels: FeatureCollection | undefined = darkVessels
            ? {
                  type: 'FeatureCollection',
                  features: darkVessels.features.filter(
                      (feature) => feature.properties?.object_class_score >= detectionConfidence / 100
                  ),
              }
            : undefined;
        return [
            ...(matchedVessels?.features || []),
            ...(filtered_dark_vessels?.features || []),
            ...(aisPositions?.features || []),
            ...(staticObjects?.features || []),
        ];
    }, [aisPositions, matchedVessels, darkVessels, staticObjects, detectionConfidence]);

    const tagsFrequency: Record<string, number> = useMemo(() => {
        const tags: Record<string, number> = {};
        if (all_positions) {
            all_positions.forEach((feature) => {
                if (feature.properties?.tags) {
                    return feature.properties.tags.forEach((tag: string) => {
                        if (tags[tag]) {
                            tags[tag] += 1;
                        } else {
                            tags[tag] = 1;
                        }
                    });
                }
            });
            return tags;
        }
        return {};
    }, [all_positions]);

    const noTag = 'No Tag';
    const awaitingTag = 'Awaiting Tag';
    const initialFilterState: FilterButton[] = useMemo(() => {
        const tags = all_positions.flatMap((feature) => feature.properties?.tags || []);
        tags.push(noTag, awaitingTag);
        const uniqueTags = [...new Set(tags)];
        return uniqueTags.map((tag) => ({
            children: tag,
            variant: elevatedTags.includes(tag) ? 'warning' : 'neutral',
            size: 'small',
            subtle: true,
            count: tag in tagsFrequency ? tagsFrequency[tag] : undefined,
        }));
    }, [all_positions, tagsFrequency]);
    const [filterState, setFilterState] = useState<FilterButton[]>(initialFilterState);

    useEffect(() => {
        setFilterState(initialFilterState);
    }, [initialFilterState]);

    const filteredPositions = useMemo(() => {
        const activeTags = filterState.filter((tag) => !tag.subtle);
        if (activeTags.length === 0) {
            return all_positions;
        }
        const selectedTags = activeTags.map((tag) => tag.children);
        const hasNoTagFilter = selectedTags.includes(noTag);
        return all_positions.filter((event) => {
            if (event.properties?.tags) {
                const matchesNoTagFilter = hasNoTagFilter && event.properties?.tags.length === 0;
                const matchesTags = event.properties?.tags.some((eventTag: string) => selectedTags.includes(eventTag));
                return matchesTags || matchesNoTagFilter;
            }
            return selectedTags.includes(awaitingTag);
        });
    }, [all_positions, filterState]);

    const onFilterToogle = () => {
        setIsFilterOpen((prev) => !prev);
    };

    useEffect(() => {
        if (filteredPositions) {
            setPositions(filteredPositions);
            setNumCaptures(filteredPositions.length);
            setEndIndex(filteredPositions.length >= 30 ? 30 : filteredPositions.length);
            setStartIndex(0);
        }
    }, [filteredPositions]);

    return (
        <div className="absolute z-10 top-16 right-2 w-1/5 min-w-[175px] max-w-[375px] bg-background-primary">
            {!selectedFeature ? (
                <BorderFreeRounded>
                    <Border>
                        <VText variant="H2">Overwatch</VText>
                        <VText>{numCaptures ? numCaptures + ' satellite images' : '  '}</VText>
                        <div className="flex">
                            {isFilterOpen ? (
                                <TagFilter
                                    filter={filterState}
                                    setFilter={setFilterState}
                                    onClose={onFilterToogle}
                                    onApply={onFilterToogle}
                                />
                            ) : (
                                <Button
                                    onClick={onFilterToogle}
                                    text="Filter selection"
                                    iconAfter={<SlidersHorizontal size={16} />}
                                    variant="neutral"
                                    size="small"
                                />
                            )}
                        </div>
                    </Border>
                    <ImageHolder numItems={numCaptures || 0}>
                        {filteredPositions.slice(startIndex, endIndex).map((item, index) => {
                            const isSentinel = item.properties?.image_source !== 'planet_scope'; // TODO: Needs to be updated if we add more sources
                            return (
                                <ImageObjectWrapper
                                    key={index}
                                    $isSentinel={isSentinel}
                                    onClick={() => {
                                        setSelectedFeature(item);
                                    }}
                                    onMouseEnter={() => setHoveredFeature(item)}
                                    onMouseLeave={() => setHoveredFeature(null)}
                                >
                                    <ImageWrapper>
                                        {isSentinel ? (
                                            <ImageTransform farNorthScaling={item.properties?.lat}>
                                                {item.properties?.image_path ? (
                                                    <DetectionImage image_path={item.properties.image_path} />
                                                ) : (
                                                    <TargetOfInterestImage
                                                        imageSource={item.properties?.image_source}
                                                        lon={item.properties?.lon}
                                                        lat={item.properties?.lat}
                                                        timestamp={item.properties?.timestamp}
                                                        mmsi={item.properties?.mmsi}
                                                    />
                                                )}
                                            </ImageTransform>
                                        ) : item.properties?.image_path ? (
                                            <PlanetThumbnail image_path={item.properties.image_path} />
                                        ) : null}
                                    </ImageWrapper>
                                    {getLegendDot(item)}
                                    <ColumnWrapper>
                                        <div className="flex gap-2 items-center">
                                            <VText>
                                                {item.properties?.mmsi ? mmsiToFlag(item.properties?.mmsi) : null}
                                            </VText>
                                            <VText>{item.properties?.name || item.properties?.mmsi}</VText>
                                        </div>
                                        <DateInfo>
                                            {typeof item.properties?.timestamp === 'string' ? (
                                                <VText>{getDateTimeText(new Date(item.properties?.timestamp))}</VText>
                                            ) : null}
                                        </DateInfo>
                                        <TagView tags={item.properties?.tags} />
                                    </ColumnWrapper>
                                </ImageObjectWrapper>
                            );
                        })}
                    </ImageHolder>
                    {numCaptures ? (
                        <>
                            <div className="flex justify-center p-2">
                                <VText>
                                    Images {startIndex + 1} to {endIndex}
                                </VText>
                            </div>
                            <div className="flex justify-center gap-2 p-2">
                                <Arrow
                                    disabled={startIndex === 0}
                                    onClick={() => {
                                        const newStartIndex =
                                            startIndex - buttonIncrement >= 0 ? startIndex - buttonIncrement : 0;
                                        const newEndIndex =
                                            newStartIndex <= 0
                                                ? buttonIncrement > positions.length
                                                    ? positions.length
                                                    : buttonIncrement
                                                : endIndex - buttonIncrement;

                                        setStartIndex(newStartIndex);
                                        setEndIndex(newEndIndex);
                                    }}
                                >
                                    <img src="/images/icons/ArrowLeft.svg" alt="Previous date" />
                                </Arrow>
                                <Arrow
                                    disabled={endIndex === positions.length}
                                    onClick={() => {
                                        const newEndIndex =
                                            endIndex + buttonIncrement <= numCaptures
                                                ? endIndex + buttonIncrement
                                                : numCaptures;
                                        const newStartIndex =
                                            newEndIndex >= numCaptures
                                                ? numCaptures - buttonIncrement < 0
                                                    ? 0
                                                    : numCaptures - buttonIncrement
                                                : startIndex + buttonIncrement;

                                        setStartIndex(newStartIndex);
                                        setEndIndex(newEndIndex);
                                    }}
                                >
                                    <img src="/images/icons/ArrowRight.svg" alt="arrow" />
                                </Arrow>
                            </div>
                        </>
                    ) : null}
                </BorderFreeRounded>
            ) : (
                <VesselInfo selectedFeature={selectedFeature} setSelectedFeature={setSelectedFeature} />
            )}
        </div>
    );
};

export default OverwatchRight;
